<template>
  <RevForm
    class="w-full max-w-full"
    :initial-values
    :validate
    @submit="(values) => emit('submit', values)"
  >
    <template #default="{ values, errors }">
      <div class="grid grid-cols-3 gap-24">
        <div class="col-span-2">
          <RevInputFileUpload
            id="evidence-upload-file-input"
            v-model="values.attachments"
            :accept="ACCEPTED_FILE_EXTENSIONS"
            :description="getDescriptionOrError(errors.attachments)"
            :disabled="isLoading"
            :errors="fileErrors"
            :label-empty="uploadLabel"
            :max-file-size="MAX_FILE_SIZE"
            :max-files="MAX_FILE_COUNT"
            :max-files-size="MAX_FILES_SIZE"
            @errors="handleErrors"
            @update:model-value="handleAttachmentsChange"
          />

          <div v-if="hasUploadErrors" class="mt-8">
            <ul
              class="text-action-danger-hi text-body-2"
              data-test="upload-errors-list"
            >
              <li v-for="err in uploadErrors" :key="err.message">
                {{ err.message }}
              </li>
            </ul>
          </div>
        </div>
        <div class="col-span-1">
          <slot />
        </div>
      </div>

      <div class="mt-24 space-x-4" :class="{ 'mb-8': isMessageSectionOpen }">
        <RevLink @click="() => (isMessageSectionOpen = !isMessageSectionOpen)">
          {{ i18n(translations.addDetailsLink) }}
        </RevLink>
        <span class="body-1">{{ i18n(translations.optional) }}</span>
      </div>

      <RevInputTextarea
        v-if="isMessageSectionOpen"
        id="evidence-upload-text-input"
        v-model="values.message"
        :error="errors.message"
        :label="i18n(translations.textareaLabel)"
        name="message"
      />

      <div class="relative mt-24 flex items-center gap-16">
        <NextTaskCheckbox
          v-if="hasNextTask && FF_ENABLE_CUSTOMER_CARE_REQUEST_NAVIGATION"
          v-model="values.isNextTaskChecked"
        />

        <RevButton
          :disabled="isLoading"
          :loading="isLoading"
          type="submit"
          variant="primary"
        >
          {{ i18n(translations.submitButton) }}
        </RevButton>
      </div>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { computed, nextTick, reactive, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { humanReadableSize } from '@backmarket/utils/math/humanReadableSize'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import type { FileError } from '@ds/components/InputFileUpload/InputFileUpload.type'
import { RevInputTextarea } from '@ds/components/InputTextarea'
import { RevLink } from '@ds/components/Link'

import type { OrderlineOpenTask } from '~/api/seller-after-sales/types'

import NextTaskCheckbox from '../../../../NextTaskCheckbox/NextTaskCheckbox.vue'

import {
  ACCEPTED_FILE_EXTENSIONS,
  ERROR_TYPES,
  MAX_FILES_SIZE,
  MAX_FILE_COUNT,
  MAX_FILE_SIZE,
} from './UploadEvidence.constants'
import translations from './UploadEvidence.translations'

const i18n = useI18n()
const { FF_ENABLE_CUSTOMER_CARE_REQUEST_NAVIGATION } = useRuntimeConfig().public

const { uploadLabel, mostUrgentTask, uploadErrors } = defineProps<{
  uploadLabel: string
  isLoading: boolean
  mostUrgentTask?: OrderlineOpenTask
  uploadErrors?: Array<Error>
}>()

const fileErrors = ref<FileError[]>([])
const shouldClearErrorsOnNextChange = ref(false)
const isSettingErrors = ref(false)

const emit = defineEmits(['submit', 'clear-errors'])

const hasNextTask = computed(() => Boolean(mostUrgentTask))

const hasUploadErrors = computed(() => uploadErrors && uploadErrors.length > 0)

type Values = {
  attachments: File[]
  message: string
  isNextTaskChecked: boolean
}

const initialValues = reactive<Values>({
  attachments: [],
  message: '',
  isNextTaskChecked: hasNextTask.value,
})

const isMessageSectionOpen = ref(false)

const validate = makeValidate<Values>({
  attachments: [required(i18n(translations.requiredMessageError))],
})

function handleErrors(errors: FileError[]) {
  // Prevent errors from being cleared in the same synchronous cycle
  isSettingErrors.value = true
  fileErrors.value = errors
  if (errors.length > 0) {
    shouldClearErrorsOnNextChange.value = true
  }
  // Why we need the nextTick trick: The RevInputFileUpload component fires @errors and
  // @update:model-value events in the same synchronous execution cycle. Without proper timing
  // control, errors would be cleared immediately after being set.
  // Errors should only be cleared when users take action (selecting new
  // files) AFTER seeing the validation messages, not immediately when validation fails.
  nextTick(() => {
    isSettingErrors.value = false
  })
}

function handleAttachmentsChange() {
  // Only clear errors if they were previously set AND we're not currently setting them
  // This prevents clearing errors in the same event cycle they're being set
  if (shouldClearErrorsOnNextChange.value && !isSettingErrors.value) {
    fileErrors.value = []
    shouldClearErrorsOnNextChange.value = false
  }
  if (hasUploadErrors.value) {
    emit('clear-errors')
  }
}

const fileErrorsLabel = {
  [ERROR_TYPES.MAX_FILES_EXCEEDED]: i18n(translations.fileInputErrorMaxFiles, {
    count: MAX_FILE_COUNT,
  }),
  [ERROR_TYPES.MAX_FILE_SIZE_EXCEEDED]: i18n(
    translations.fileInputErrorMaxSize,
    { maxSize: humanReadableSize(MAX_FILE_SIZE, 'B') },
  ),
  [ERROR_TYPES.MAX_FILES_SIZE_EXCEEDED]: i18n(
    translations.fileInputErrorMaxFilesSize,
    { maxSize: humanReadableSize(MAX_FILES_SIZE, 'B') },
  ),
  [ERROR_TYPES.WRONG_FILE_EXTENSION]: i18n(translations.fileInputErrorMimeType),
}

const formattedFileErrors = computed(() => {
  const uniqueErrorTypes = [
    ...new Set(fileErrors.value.map((error) => error.type)),
  ]

  return uniqueErrorTypes.map((type) => fileErrorsLabel[type]).join('; ')
})

function getDescriptionOrError(error?: string) {
  return (
    formattedFileErrors.value ||
    error ||
    i18n(translations.fileInputDescription, {
      count: MAX_FILE_COUNT,
      maxSize: humanReadableSize(MAX_FILE_SIZE, 'B'),
    })
  )
}
</script>
